import { readInitialServiceProfessionals, type InitialServiceProfessional } from "../core/appointmentInitialServiceProfessionals";
import { readAppointmentResourceOnlyExecutionV1 } from "@/app/booking/core/appointmentResourceOnlyExecutionV1";
import { getAppointmentStaffRequiredTasks } from "@/app/booking/scheduling/appointmentStaffRequiredTasks";
import {
  createAppointmentServicesDraft,
  type AppointmentServicesDraftFailureReason,
  type AppointmentServicesDraftGroupGuestInput,
  type CreateAppointmentServicesDraftResult,
} from "@/app/booking/adapters/appointmentServicesDraft";
import {
  resolveLocationAppointmentCatalog,
  resolveLocationAppointmentTaskProfessionals,
  resolveLocationAppointmentTaskProfessionalsFromCatalog,
  validateLocationAppointmentCatalogEmbeddedEligibility,
  type LocationAppointmentCatalog,
  type LocationAppointmentCatalogSource,
} from "@/app/booking/adapters/locationAppointmentCatalog";
import type { AppointmentDraftMode } from "@/app/booking/core/appointmentBookingDraft";
import {
  buildAppointmentSchedulePlan,
  type AppointmentScheduleTaskRequirement,
} from "@/app/booking/scheduling/appointmentSchedulePlan";
import {
  applyPerServiceStaffChoice,
} from "@/app/booking/scheduling/appointmentStaffPreferences";
import type {
  SpecialistAssignment,
  SpecialistService,
  SpecialistServiceOffering,
  SpecialistServiceSelection,
  SpecialistWorkplace,
} from "@/app/specialists/model";

export type LocationServicesAppointmentDraftFailureReason =
  AppointmentServicesDraftFailureReason;

export type CreateLocationServicesAppointmentDraftResult =
  CreateAppointmentServicesDraftResult;

export type LocationServicesAppointmentGroupGuestInput =
  AppointmentServicesDraftGroupGuestInput;

export interface CreateLocationServicesAppointmentDraftInput {
  draftId: string;
  mode: AppointmentDraftMode;
  locationId: string;
  /** Exact provider-authored owner of the canonical Location catalog rows. */
  catalogAssignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
  selections: readonly SpecialistServiceSelection[];
  groupGuests?: readonly LocationServicesAppointmentGroupGuestInput[];
  /**
   * Exact provider snapshot owner used to validate this Location catalog.
   * Callers rebuilding a draft must preserve it through every nested catalog
   * lookup instead of silently falling back to frontend fixtures.
   */
  catalogSource?: LocationAppointmentCatalogSource;
  /**
   * Serializable route-owned snapshot for client coordinators. It is mutually
   * exclusive with `catalogSource` and preserves the exact eligibility proof
   * that produced the rendered catalog.
   */
  catalogSnapshot?: LocationAppointmentCatalog;
  /**
   * A Location-owned, changeable initial preference carried from an embedded
   * employee profile. It never converts the draft to Specialist origin.
   */
  initialProfessionalUserLocationId?: string | null;
  initialServiceProfessionals?: readonly InitialServiceProfessional[];
}

function hasSameSerializableStructure(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

type LocationInitialProfessionalTask = Pick<
  AppointmentScheduleTaskRequirement,
  "bundle" | "lineId" | "participantId" | "taskId"
>;

/**
 * An embedded employee is only a Location-owned initial preference. Parallel
 * bundle children occupy the same time window, so one professional may seed
 * at most the first eligible child in each participant/line group.
 */
export function selectLocationInitialProfessionalSeedTaskIds(
  tasks: readonly LocationInitialProfessionalTask[],
  eligibleTaskIds: ReadonlySet<string>,
) {
  const seededParallelGroups = new Set<string>();

  return tasks.flatMap((task) => {
    if (!eligibleTaskIds.has(task.taskId)) {
      return [];
    }

    if (task.bundle?.scheduleType !== "parallel") {
      return [task.taskId];
    }

    const groupKey = JSON.stringify([task.participantId, task.lineId]);

    if (seededParallelGroups.has(groupKey)) {
      return [];
    }

    seededParallelGroups.add(groupKey);
    return [task.taskId];
  });
}

/**
 * Location-owned Step-1 boundary. The catalog owner proves each configured
 * offering, while staff remains unresolved until the shared Professional
 * stage resolves an exact customer choice or provider-authorized strategy.
 */
export function createLocationServicesAppointmentDraft({
  locationId,
  catalogAssignment,
  catalogSource,
  catalogSnapshot,
  initialProfessionalUserLocationId,
  initialServiceProfessionals,
  ...input
}: CreateLocationServicesAppointmentDraftInput): CreateLocationServicesAppointmentDraftResult {
  if (
    (catalogSnapshot && catalogSource) ||
    (catalogSnapshot &&
      (!validateLocationAppointmentCatalogEmbeddedEligibility(
        catalogSnapshot,
      ) ||
        catalogSnapshot.workplace.locationId !== locationId ||
        !hasSameSerializableStructure(
          catalogSnapshot.catalogAssignment,
          catalogAssignment,
        ) ||
        !hasSameSerializableStructure(
          catalogSnapshot.workplace,
          input.workplace,
        ) ||
        !hasSameSerializableStructure(
          catalogSnapshot.services,
          input.services,
        ) ||
        !hasSameSerializableStructure(
          catalogSnapshot.offerings,
          input.offerings,
        )))
  ) {
    return { ok: false, reason: "invalid-catalog" };
  }

  const hasResourceOnly = input.offerings.some(offering => readAppointmentResourceOnlyExecutionV1(offering) !== undefined);
  const resourceCatalog = hasResourceOnly ? catalogSnapshot ?? resolveLocationAppointmentCatalog(locationId, catalogSource) : null;
  if (hasResourceOnly && (!resourceCatalog || input.mode !== "individual" || !hasSameSerializableStructure(resourceCatalog.catalogAssignment, catalogAssignment) || !hasSameSerializableStructure(resourceCatalog.workplace, input.workplace) || !hasSameSerializableStructure(resourceCatalog.services, input.services) || !hasSameSerializableStructure(resourceCatalog.offerings, input.offerings))) return { ok: false, reason: "invalid-catalog" };

  const serviceProfessionals = initialProfessionalUserLocationId || input.mode !== "individual"
    ? [] : (readInitialServiceProfessionals(initialServiceProfessionals) ?? []).filter(row =>
      input.selections.some(selection => selection.serviceId === row.serviceId) &&
      input.services.some(service => service.serviceId === row.serviceId && service.kind === "service"));
  const created = createAppointmentServicesDraft({
    ...input,
    origin: {
      kind: "location-services",
      locationId,
      catalogAssignment,
      ...(resourceCatalog?.eligibilityPublication.resourceOnlyExecutionsV1 ? { resourceOnlyExecutionsV1: resourceCatalog.eligibilityPublication.resourceOnlyExecutionsV1 } : {}),
      ...(serviceProfessionals.length ? { initialServiceProfessionals: serviceProfessionals } : {}),
      ...(initialProfessionalUserLocationId
        ? { initialProfessionalUserLocationId }
        : {}),
    },
  });

  if (!created.ok || (!initialProfessionalUserLocationId && serviceProfessionals.length === 0)) {
    return created;
  }

  const catalog =
    catalogSnapshot ??
    resolveLocationAppointmentCatalog(locationId, catalogSource);

  if (
    !catalog ||
    !hasSameSerializableStructure(catalog.catalogAssignment, catalogAssignment) ||
    !hasSameSerializableStructure(catalog.workplace, input.workplace) ||
    !hasSameSerializableStructure(catalog.services, input.services) ||
    !hasSameSerializableStructure(catalog.offerings, input.offerings) ||
    (serviceProfessionals.length === 0 && !catalog.professionals.some(
      ({ userLocationId }) =>
        userLocationId === initialProfessionalUserLocationId,
    ))
  ) {
    return { ok: false, reason: "invalid-catalog" };
  }

  const planResult = buildAppointmentSchedulePlan(created.draft);

  if (!planResult.ok) {
    return { ok: false, reason: "invalid-draft" };
  }

  if (serviceProfessionals.length > 0) {
    let seeded = created.draft;
    for (const task of getAppointmentStaffRequiredTasks(planResult.plan)) {
      if (task.bundle) continue;
      const preference = serviceProfessionals.find(row => row.serviceId === task.serviceId);
      if (!preference) continue;
      const professionals = catalogSnapshot
        ? resolveLocationAppointmentTaskProfessionalsFromCatalog(catalog, task)
        : resolveLocationAppointmentTaskProfessionals(catalog, task, catalogSource);
      if (!professionals?.some(row => row.userLocationId === preference.userLocationId)) continue;
      const next = applyPerServiceStaffChoice(seeded, planResult.plan, task.taskId, preference.userLocationId);
      if (next) seeded = next;
    }
    return { ok: true, draft: seeded };
  }

  if (!initialProfessionalUserLocationId) return created;

  const eligibleTaskIds = new Set(
    getAppointmentStaffRequiredTasks(planResult.plan).flatMap((task) => {
      const taskIdentity = {
        serviceId: task.serviceId,
        offeringId: task.offeringId,
      };
      const professionals = catalogSnapshot
        ? resolveLocationAppointmentTaskProfessionalsFromCatalog(
            catalog,
            taskIdentity,
          )
        : resolveLocationAppointmentTaskProfessionals(
            catalog,
            taskIdentity,
            catalogSource,
          );

      return professionals?.some(
        ({ userLocationId }) =>
          userLocationId === initialProfessionalUserLocationId,
      )
        ? [task.taskId]
        : [];
    }),
  );

  if (created.draft.mode === "individual") {
    if (eligibleTaskIds.size !== getAppointmentStaffRequiredTasks(planResult.plan).length) {
      return created;
    }

    const seedTaskIds = new Set(
      selectLocationInitialProfessionalSeedTaskIds(
        planResult.plan.tasks,
        eligibleTaskIds,
      ),
    );
    let seeded = created.draft;

    for (const task of planResult.plan.tasks) {
      if (!seedTaskIds.has(task.taskId)) {
        continue;
      }

      const next = applyPerServiceStaffChoice(
        seeded,
        planResult.plan,
        task.taskId,
        initialProfessionalUserLocationId,
      );

      if (!next) {
        return { ok: false, reason: "invalid-draft" };
      }

      seeded = next;
    }

    return { ok: true, draft: seeded };
  }

  let seeded = created.draft;
  const organizerTasks = planResult.plan.tasks.filter(
    (task) =>
      task.participantRole === "organizer" &&
      task.participantId === created.draft.organizerParticipantId,
  );
  const seedTaskIds = new Set(
    selectLocationInitialProfessionalSeedTaskIds(
      organizerTasks,
      eligibleTaskIds,
    ),
  );

  for (const task of organizerTasks) {
    if (!seedTaskIds.has(task.taskId)) {
      continue;
    }

    const next = applyPerServiceStaffChoice(
      seeded,
      planResult.plan,
      task.taskId,
      initialProfessionalUserLocationId,
    );

    if (next) {
      seeded = next;
    }
  }

  return { ok: true, draft: seeded };
}
